import { Component, OnDestroy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DxDataGridComponent } from 'devextreme-angular';
import { ITaskNavigationFilterArgs } from '../../types/dashboard-types';



@Component({
  standalone: false,
  selector: 'app-dashboard-table',
  templateUrl: './dashboard-table.component.html',
  styleUrls: ['./dashboard-table.component.css']
})
export class DashboardTableComponent implements OnDestroy {
  @ViewChild('dxDataGrid', { static: false }) dataGrid: DxDataGridComponent;

  @Input() data: any[] = [];
  @Input() columns: any[] = [];
  @Input() title: string = '';

  readonly allowColumnResizing = true;
  readonly columnResizingMode = 'widget';
  readonly columnAutoWidth = false;

  private readonly boundOnGridWheel = this.onGridWheel.bind(this);
  private gridScrollContainer: HTMLElement | null = null;
  private wheelListenerAttached = false;

  getColumnWidth(index: number): string | undefined {
    // First column has no fixed width so it absorbs all remaining grid space.
    // Count columns get a fixed width wide enough for the status pill headers.
    return index === 0 ? undefined : '120';
  }

  getColumnMinWidth(index: number): number {
    // Count columns: the longest pill captions ("Completed"/"Cancelled") plus DevExtreme's
    // header cell padding need ~110px; below this, DevExtreme's `.dx-header-row > td`
    // (overflow: hidden) clips the pill instead of wrapping it, since allowColumnResizing
    // shrinks columns toward minWidth on narrow (e.g. two-table-per-row) layouts.
    // First column: prevent label from being dragged too narrow.
    return index === 0 ? 150 : 110;
  }

  @Output() onValueClicked : EventEmitter<ITaskNavigationFilterArgs> = new EventEmitter();

  constructor(private router: Router) {}

  ngOnDestroy(): void {
    this.dataGrid?.instance?.element()?.removeEventListener('wheel', this.boundOnGridWheel, true);
  }

  // Attaches the horizontal wheel/trackpad fix once DevExtreme's scrollable container exists in the DOM.
  onGridContentReady(): void {
    if (this.wheelListenerAttached) {
      return;
    }

    const gridElement = this.dataGrid?.instance?.element();
    this.gridScrollContainer = gridElement?.querySelector('.dx-scrollable-container') ?? null;
    if (!gridElement || !this.gridScrollContainer) {
      return;
    }

    // Capture phase runs before DevExtreme's own (bubble-phase) wheel handler on the scrollable
    // container, so stopPropagation here prevents DevExtreme from also applying its vertical
    // interpretation of the same horizontal wheel/trackpad gesture.
    gridElement.addEventListener('wheel', this.boundOnGridWheel, { capture: true, passive: false });
    this.wheelListenerAttached = true;
  }

  // A dedicated horizontal mouse wheel or trackpad swipe reports as deltaX with little/no deltaY;
  // only intercept that case so a normal vertical wheel gesture is left to scroll the rows natively.
  private onGridWheel(e: WheelEvent): void {
    const absDeltaX = Math.abs(e.deltaX);
    const absDeltaY = Math.abs(e.deltaY);
    if (absDeltaX === 0 || absDeltaX <= absDeltaY || !this.gridScrollContainer) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    this.gridScrollContainer.scrollLeft += e.deltaX;
  }

  getHeaderTemplate(dataField: string): string {
    const statusColumnMap: { [key: string]: string } = {
      'openCount': 'openHeader',
      'pendingCount': 'pendingHeader',
      'holdCount': 'holdHeader',
      'overDueCount': 'overdueHeader',
      'rejectedCount': 'rejectedHeader',
      'cancelledCount': 'cancelledHeader',
      'completedCount': 'completedHeader',
      'totalCount': 'Total'
    };

    return statusColumnMap[dataField] || '';
  }

  onCellClick(e: any) {
    if (e.column && ['openCount', 'pendingCount', 'holdCount', 'overDueCount', 'completedCount', 'rejectedCount', 'cancelledCount'
    , 'totalCount'].includes(e.column.dataField)) {
      const taskIdsKey = e.column.dataField === 'openCount'
        ? 'openTaskIds'
        : e.column.dataField === 'overDueCount'
        ? 'overDueTaskIds'
        : e.column.dataField === 'completedCount'
        ? 'completedTaskIds'
        : e.column.dataField === 'pendingCount'
        ? 'pendingTaskIds'
        : e.column.dataField === 'holdCount'
        ? 'holdTaskIds'
        : e.column.dataField === 'rejectedCount'
        ? 'rejectedTaskIds'
        : e.column.dataField === 'cancelledCount'
        ? 'cancelledTaskIds'
        : e.column.dataField === 'totalCount'
        ? 'all'
        : 'all';

      let taskIds: any[];
      if (taskIdsKey === 'all') {
        // Concatenate all task ID arrays from e.data
        taskIds = [
          ...(e.data.openTaskIds || []),
          ...(e.data.pendingTaskIds || []),
          ...(e.data.holdTaskIds || []),
          ...(e.data.overDueTaskIds || []),
          ...(e.data.completedTaskIds || []),
          ...(e.data.cancelledTaskIds || []),
          ...(e.data.rejectedTaskIds || [])
        ];
      } else {
        taskIds = e.data[taskIdsKey];
      }

      this.onValueClicked.emit({
        taskIds: taskIds
      });
    }
  }

  getCellToolTip(cellData: any): string {
    const taskIdsKey = cellData.column.dataField === 'openCount'
      ? 'openTaskIds'
      : cellData.column.dataField === 'pendingCount'
      ? 'pendingTaskIds'
      : cellData.column.dataField === 'holdCount'
      ? 'holdTaskIds'
      : cellData.column.dataField === 'overDueCount'
      ? 'overDueTaskIds'
      : cellData.column.dataField === 'completedCount'
      ? 'completedTaskIds'
      : cellData.column.dataField === 'rejectedCount'
      ? 'rejectedTaskIds'
      : 'cancelledTaskIds';

    return `Tasks: ${cellData.data[taskIdsKey].join(', ')}`;
  }
}
